'use client';

import React, { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { xor, capitalize } from 'lodash';
import { interpolate } from '@format/interpolate';
import { IconButton } from '@ui/button';
import { VisuallyHidden } from '@web/atoms';
import { ResponsiveImage, Carousel } from '@web/molecules';
import {
  IngredientModal,
  useResolvedIngredientModal,
  ProductSelector,
} from '@web/components';
import {
  userViewMealPlanTypeScreen,
  userViewPlanDetailsPage,
} from '@web/account/MealPrepTypeSelectionForm/events';
import { CMSBlok } from '../../cms';
import { PRODUCT_TYPE_HALF_FRESH } from '../../products/constants';
import { CheckoutBar } from '../CheckoutBar';
import { useReporter, useElevarReporter } from '../../reporter';
import { useShopifyCartContext } from '../OnboardingFlowContainer/ShopifyCartProvider';
import { buildSelectedElevarMealPrepProduct } from '../OnboardingFlowContainer/helpers';
import { useSessionCustomer } from '../../customer';
import {
  userContinuesFromRecommendedPlan,
  recipeSelected,
  recipeUnselected,
  userExpandsFreshRecipeDetails,
  mealPlanTypeSelected,
} from './events.js';
import {
  Wrapper,
  StyledRecommendedPlanContainer,
  StyledForm,
  ProductSelectorContainer,
  ProductSelectorContainerDesktop,
  ProductSelectorContainerMobile,
  Headline,
  ProductCardContainer,
  Description,
  DesktopImageContainer,
  ContentContainer,
  IconButtonContainer,
  MobileImageContainer,
  RatingContainer,
  StarRating,
  CalloutsContainer,
  CallloutText,
  CalloutContainer,
  DescriptionContainer,
  CarouselSectionContainer,
  TestimonialScrollContainer,
  CollapsedPlansContainer,
  SectionHeader,
  ReviewsText,
} from './styles';

export const CarouselMealPlanForm = ({
  onSubmit,
  petName,
  headline,
  description,
  modals,
  recipeModalsList,
  ctaText = 'Continue',
  freshRecipes,
  defaultFreshRecipes,
  bakedRecipes,
  defaultBakedRecipes,
  defaultMealPrepType,
  mealPrepTypes,
  unavailableMealPrepTypes,
  collapsedMealPrepTypes,
  onBackButtonClick,
  unavailableFreshRecipes,
  unavailableBakedRecipes,
  period,
  getConfigFromRecipes,
  body,
  notSelectableText,
  ctaButtonVariant = 'yellow',
  isWithinOptimized = false,
  starColor,
  recipeSelectionText = 'Choose up to 4 recipes',
  callouts,
}) => {
  const [selectedMealPrepType, setSelectedMealPrepType] =
    useState(defaultMealPrepType);
  const [selectedFreshRecipes, setSelectedFreshRecipes] =
    useState(defaultFreshRecipes);
  const [selectedBakedRecipes, setSelectedBakedRecipes] =
    useState(defaultBakedRecipes);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const viewEventsFired = useRef(false);
  const elevarViewItemsTracked = useRef(false);

  const reporter = useReporter();
  const elevarReporter = useElevarReporter();

  const { customer, isAppSession } = useSessionCustomer();
  const { cart } = useShopifyCartContext();
  const customerEmail = cart?.customer?.email || customer?.email;

  const elevarUserProperties = useMemo(() => {
    const props = {};
    if (customerEmail) props.customer_email = customerEmail;
    if (customer?.id) props.customer_id = customer.id;
    return props;
  }, [customerEmail, customer?.id]);

  const allMealPrepProducts = useMemo(
    () => [...(mealPrepTypes || []), ...(collapsedMealPrepTypes || [])],
    [mealPrepTypes, collapsedMealPrepTypes],
  );

  const carouselAssets = useMemo(() => {
    return (
      [...(mealPrepTypes || []), ...(collapsedMealPrepTypes || [])].find(
        plan => {
          return plan.value === selectedMealPrepType;
        },
      )?.carousel_assets || []
    );
  }, [mealPrepTypes, selectedMealPrepType, collapsedMealPrepTypes]);

  const { modal, onRecipeDetailsClick } = useResolvedIngredientModal(
    modals,
    recipeModalsList,
  );

  useEffect(() => {
    if (!viewEventsFired.current) {
      if (isWithinOptimized) {
        reporter.tag(userViewPlanDetailsPage());
      } else {
        reporter.tag(userViewMealPlanTypeScreen());
      }
      viewEventsFired.current = true;
    }
    if (
      selectedMealPrepType === 'fresh' ||
      selectedMealPrepType === 'half_fresh'
    ) {
      setSelectedBakedRecipes([]);
    } else {
      setSelectedBakedRecipes(defaultBakedRecipes);
    }
  }, [
    reporter,
    cart,
    selectedMealPrepType,
    defaultBakedRecipes,
    isWithinOptimized,
  ]);

  useEffect(() => {
    if (elevarViewItemsTracked.current || !mealPrepTypes?.length) return;
    const normalizedProducts = allMealPrepProducts
      .map(planType =>
        buildSelectedElevarMealPrepProduct({
          mealPrepProducts: allMealPrepProducts,
          mealPrepTypes,
          selectedMealPrepType: planType.value,
          selectedFreshRecipes,
          selectedBakedRecipes,
          getConfigFromRecipes,
        }),
      )
      .filter(Boolean);

    normalizedProducts.forEach(product => {
      elevarReporter?.elevarViewItem({
        planType: product,
        userProperties: elevarUserProperties,
      });
    });
    elevarViewItemsTracked.current = true;
  }, [
    allMealPrepProducts,
    elevarReporter,
    elevarUserProperties,
    selectedFreshRecipes,
    selectedBakedRecipes,
    getConfigFromRecipes,
    mealPrepTypes,
  ]);

  const onFreshChange = freshRecipes => {
    const currentRecipes = selectedFreshRecipes ?? [];
    const recipeAdded = freshRecipes.length > currentRecipes.length;
    const recipe = xor(freshRecipes, currentRecipes)[0];
    const event = recipeAdded ? recipeSelected : recipeUnselected;
    reporter.tag(event(recipe));
    setSelectedFreshRecipes(freshRecipes);
  };

  const onBakedChange = bakedRecipes => {
    const currentRecipes = selectedBakedRecipes ?? [];
    const recipeAdded = bakedRecipes.length > currentRecipes.length;
    const recipe = xor(bakedRecipes, currentRecipes)[0];
    const event = recipeAdded ? recipeSelected : recipeUnselected;
    reporter.tag(event(recipe));
    setSelectedBakedRecipes(bakedRecipes);
  };

  const onMealPrepChange = product => {
    reporter.tag(mealPlanTypeSelected(product[0]));
    if (unavailableMealPrepTypes?.includes(product[0])) return null;
    setSelectedMealPrepType(product[0]);
  };

  const isValid =
    selectedMealPrepType === 'fresh' || selectedMealPrepType === 'half_fresh'
      ? selectedFreshRecipes && selectedFreshRecipes.length > 0
      : selectedFreshRecipes &&
        selectedFreshRecipes.length > 0 &&
        selectedBakedRecipes &&
        selectedBakedRecipes.length > 0;

  const onSubmitHandler = async event => {
    event.preventDefault();
    if (!isValid) {
      return;
    }
    const email = cart?.customer?.email || customer?.email;
    reporter.tag(
      userContinuesFromRecommendedPlan(
        email,
        selectedMealPrepType,
        selectedFreshRecipes,
        selectedBakedRecipes,
      ),
    );

    const selectedProduct = buildSelectedElevarMealPrepProduct({
      mealPrepProducts: allMealPrepProducts,
      mealPrepTypes,
      selectedMealPrepType,
      selectedFreshRecipes,
      selectedBakedRecipes,
      getConfigFromRecipes,
    });
    if (selectedProduct) {
      elevarReporter?.elevarAddToCart({
        product: selectedProduct,
        userProperties: elevarUserProperties,
      });
    }

    setIsSubmitting(true);
    await onSubmit(
      selectedMealPrepType,
      selectedFreshRecipes,
      selectedBakedRecipes,
    );
  };

  const { price, discountedPrice, message } = getConfigFromRecipes(
    selectedMealPrepType,
    selectedFreshRecipes,
    selectedBakedRecipes,
  );

  const firstTestimonial = body?.find(content =>
    content.component?.includes('testimonial'),
  );

  const onRecipeDetailsHandler = product => {
    reporter.tag(userExpandsFreshRecipeDetails(product.value));
    if (product?.title?.includes('Baked')) {
      onRecipeDetailsClick('baked_' + product.value);
    } else {
      onRecipeDetailsClick(product.value);
    }
  };

  const getConfiguredPlanTypes = planTypes =>
    planTypes?.map(planType => ({
      ...planType,
      shouldShowMoreDetails: false,
      inputType: 'radio',
    }));

  const mealPrepSelectorProps = {
    selected: [selectedMealPrepType],
    unavailableProducts: unavailableMealPrepTypes,
    variant: 'radio',
    direction: 'horizontal',
    cardOrientation: 'column',
    notSelectableText: notSelectableText,
    productCardBackgroundColor: '',
    showPricePerWeek: false,
    showCheckmark: true,
    cardBorderRadius: 16,
  };

  const productSelectorProps = {
    onDetailsClick: onRecipeDetailsHandler,
    productCardVariant: 'recommended',
    cardOrientation: 'column',
    direction: 'horizontal',
    period,
    buttonIcon: 'plus',
    gridColumns: 3,
    stackOnMobile: false,
    cardWidth: 176,
    productCardBackgroundColor: 'base',
    cardTypography: 'heading5',
    showCheckmark: true,
    cardBorderRadius: 16,
  };

  const bakedSelected = selectedMealPrepType === 'baked';
  const freshSelected =
    selectedMealPrepType === 'fresh' || selectedMealPrepType === 'half_fresh';
  const mixedSelected = selectedMealPrepType === 'mixed';

  const requiredItemsNotSelected =
    (bakedSelected && !selectedBakedRecipes.length) ||
    (freshSelected && !selectedFreshRecipes.length) ||
    (mixedSelected &&
      (!selectedBakedRecipes.length || !selectedFreshRecipes.length));

  const checkoutBarProps = {
    ctaText,
    isSubmitDisabled: isSubmitting || requiredItemsNotSelected,
    isBackButtonDisabled: isSubmitting,
    shown: true,
    period,
    isSubmitting: isSubmitting,
    relativePosition: isAppSession,
    price,
    discountedPrice,
    message,
    buttonVariant: ctaButtonVariant,
  };
  return (
    <Wrapper>
      <StyledForm onSubmit={onSubmitHandler}>
        <StyledRecommendedPlanContainer>
          <IconButtonContainer aria-live="polite">
            <IconButton
              aria-label="go to previous step"
              icon={{
                iconName: 'arrow-left',
                size: '24px',
                alt: 'back',
                id: 'prev_step_footer',
                color: 'hero.plum',
              }}
              onClick={onBackButtonClick}
              type="button"
              variant="lilac"
            />
          </IconButtonContainer>
          {Boolean(carouselAssets?.length) && (
            <CarouselSectionContainer>
              <DesktopImageContainer>
                <Carousel
                  showDots={false}
                  withThumbnails={true}
                  showArrows={false}
                  borderRadius={24}
                  thumbBorderRadius={16}
                  verticalPadding={0}
                  thumbAlign="start"
                  thumbBorderColor="secondary.lilac"
                >
                  {carouselAssets?.map((item, index) => (
                    <ResponsiveImage {...item} key={index} />
                  ))}
                </Carousel>
              </DesktopImageContainer>
              <MobileImageContainer>
                <Carousel
                  showDots={false}
                  showArrows={false}
                  borderRadius={32}
                  verticalPadding={0}
                  thumbAlign="start"
                  slideFullWidth={true}
                  showPartialSlideMobile={true}
                  itemsToShow={2}
                >
                  {carouselAssets?.map((item, index) => (
                    <ResponsiveImage {...item} key={index} />
                  ))}
                </Carousel>
              </MobileImageContainer>
            </CarouselSectionContainer>
          )}
          <ContentContainer>
            <DescriptionContainer>
              <Headline>
                {interpolate(headline, {
                  pupName: capitalize(petName),
                  planType:
                    selectedMealPrepType === PRODUCT_TYPE_HALF_FRESH
                      ? 'half fresh'
                      : selectedMealPrepType,
                })}
              </Headline>
              {Boolean(firstTestimonial) ? (
                <RatingContainer
                  onClick={e => {
                    e.preventDefault();
                    document
                      .getElementById('testimonial')
                      ?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  <StarRating $backgroundColor={starColor}>
                    {' '}
                    <ReviewsText>20K+ 5-star reviews</ReviewsText>
                    <VisuallyHidden>{5} star rating for Ollie</VisuallyHidden>
                  </StarRating>{' '}
                </RatingContainer>
              ) : (
                <StarRating $backgroundColor={starColor}>
                  {' '}
                  20K+ 5-star reviews
                  <VisuallyHidden>{5} star rating for Ollie</VisuallyHidden>
                </StarRating>
              )}
              <Description>{description}</Description>
            </DescriptionContainer>
            {mealPrepTypes.length === 1 ? (
              <ProductSelectorContainer>
                <ProductSelector
                  gridColumns={1}
                  mobileGridColumns={1}
                  stackOnMobile={false}
                  products={getConfiguredPlanTypes(mealPrepTypes)}
                  unavailableProducts={unavailableMealPrepTypes}
                  onChange={onMealPrepChange}
                  productCardVariant="cro"
                  imageAlign="center"
                  mobileDescriptionPosition="right"
                  cardWidth={544}
                  {...mealPrepSelectorProps}
                />
              </ProductSelectorContainer>
            ) : (
              <div>
                <ProductSelectorContainerDesktop>
                  <ProductSelector
                    gridColumns={2}
                    cardWidth={268}
                    stackOnMobile={false}
                    offsetBadge={true}
                    badgeVariant="success"
                    products={getConfiguredPlanTypes(mealPrepTypes)}
                    unavailableProducts={unavailableMealPrepTypes}
                    onChange={onMealPrepChange}
                    productCardVariant="recommended"
                    cardImgSize={72}
                    {...mealPrepSelectorProps}
                    cardOrientation="row"
                  />
                </ProductSelectorContainerDesktop>
                <ProductSelectorContainerMobile>
                  <ProductSelector
                    gridColumns={2}
                    stackOnMobile={false}
                    offsetBadge={true}
                    badgeVariant="success"
                    products={getConfiguredPlanTypes(mealPrepTypes)}
                    unavailableProducts={unavailableMealPrepTypes}
                    onChange={onMealPrepChange}
                    productCardVariant="recommended"
                    cardImgSize={72}
                    alignColumnContent="center"
                    cardHeight={180}
                    cardWidth={176}
                    gapSize={2}
                    {...mealPrepSelectorProps}
                  />
                </ProductSelectorContainerMobile>
              </div>
            )}
            <ProductCardContainer>
              <SectionHeader>{recipeSelectionText}</SectionHeader>
              <fieldset>
                {selectedMealPrepType === 'fresh' ||
                selectedMealPrepType === 'half_fresh' ? (
                  <>
                    <ProductSelector
                      gapSize={2}
                      products={freshRecipes}
                      selected={selectedFreshRecipes}
                      unavailableProducts={unavailableFreshRecipes}
                      {...productSelectorProps}
                      onChange={onFreshChange}
                    />
                  </>
                ) : (
                  <>
                    <ProductSelector
                      gapSize={2}
                      products={freshRecipes}
                      selected={selectedFreshRecipes}
                      unavailableProducts={unavailableFreshRecipes}
                      {...productSelectorProps}
                      onChange={onFreshChange}
                    />
                    <SectionHeader>Choose up to 2 baked recipes</SectionHeader>
                    <ProductSelector
                      gapSize={2}
                      products={bakedRecipes}
                      selected={selectedBakedRecipes}
                      unavailableProducts={unavailableBakedRecipes}
                      {...productSelectorProps}
                      onChange={onBakedChange}
                    />
                  </>
                )}
              </fieldset>

              <IngredientModal {...modal} />
            </ProductCardContainer>
            {callouts?.length > 0 && (
              <CalloutsContainer>
                {callouts?.map(({ icon, text }) => (
                  <CalloutContainer key={text}>
                    <ResponsiveImage
                      src={icon.src}
                      alt={`${text} icon`}
                      width={24}
                      height={24}
                    />
                    <CallloutText>{text}</CallloutText>
                  </CalloutContainer>
                ))}
              </CalloutsContainer>
            )}
            {collapsedMealPrepTypes?.length > 0 && (
              <CollapsedPlansContainer>
                <ProductSelector
                  gridColumns={collapsedMealPrepTypes.length}
                  mobileGridColumns={collapsedMealPrepTypes.length}
                  products={getConfiguredPlanTypes(collapsedMealPrepTypes)}
                  productCardVariant="cro"
                  onChange={onMealPrepChange}
                  imageAlign="center"
                  mobileDescriptionPosition="right"
                  {...mealPrepSelectorProps}
                  showPricePerWeek={true}
                  cardWidth={544}
                />
              </CollapsedPlansContainer>
            )}
          </ContentContainer>
        </StyledRecommendedPlanContainer>
        <CheckoutBar {...checkoutBarProps} />
      </StyledForm>
      {body?.map(content => {
        if (content === firstTestimonial) {
          return (
            <TestimonialScrollContainer key={content._uid} id="testimonial">
              <CMSBlok content={content} />
            </TestimonialScrollContainer>
          );
        } else {
          return <CMSBlok key={content._uid} content={content} />;
        }
      })}
    </Wrapper>
  );
};
CarouselMealPlanForm.displayName = 'CarouselMealPlanForm';
CarouselMealPlanForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  headline: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  petName: PropTypes.string.isRequired,
  modals: PropTypes.array,
  recipeModalsList: PropTypes.array,
  ctaText: PropTypes.string.isRequired,
  freshRecipes: PropTypes.array,
  defaultFreshRecipes: PropTypes.array,
  bakedRecipes: PropTypes.array,
  defaultBakedRecipes: PropTypes.array,
  mealPrepTypes: PropTypes.array,
  defaultMealPrepTypes: PropTypes.array,
  onBackButtonClick: PropTypes.func.isRequired,
  unavailableFreshRecipes: PropTypes.array,
  unavailableBakedRecipes: PropTypes.array,
  unavailableMealPrepTypes: PropTypes.array,
  collapsedMealPrepTypes: PropTypes.array,
  period: PropTypes.string.isRequired,
  body: PropTypes.array,
  getConfigFromRecipes: PropTypes.func.isRequired,
  starColor: PropTypes.string,
  notSelectableText: PropTypes.string,
  hideHalfFresh: PropTypes.bool,
  showFreshOnly: PropTypes.bool,
  hideMixed: PropTypes.bool,
  ctaButtonVariant: PropTypes.string,
  isWithinOptimized: PropTypes.bool,
  recipeSelectionText: PropTypes.string,
  callouts: PropTypes.arrayOf(PropTypes.object),
};
